import isEmpty from 'lodash.isempty';
import { IApi } from '../../api';
import { IImage, IS3PresignedPost } from '../../api/images';
import { logging } from '../../logging';
import { entriesOf } from '../../view/helpers/typing';

export enum ImageResLimits {
	minPixelsX = 1024,
	minPixelsY = 768,
}

export enum ImageTagLimits {
	minConfidence = 95,
	maxNumVisible = 10,
}

export enum ImageScoreLimits {
	poor = 4.0,
	moderate = 4.5,
	good = 4.8,
	excellent = 5.1,
}

const imageOrientConsts = {
	numLandscape: 1,
	landscape: 'Landscape',
	portrait: 'Portrait',
	square: 'Square'
};

export const isLowRes = (image: IImage) => {
	const original = image.original.toString();
	const x = parseInt(original.substr(0, original.indexOf('x')), 10);
	const y = parseInt(original.substr(original.indexOf('x') + 1), 10);

	if (x >= y) {
		if (x < ImageResLimits.minPixelsX || y < ImageResLimits.minPixelsY) {
			return true;
		}
	} else {
		if (x < ImageResLimits.minPixelsY || y < ImageResLimits.minPixelsX) {
			return true;
		}
	}
	return false;
};

export const formatResolution = (image: IImage) => {
	return image ? image.original.toString().replace('x', ' x ') + ' px' : '';
};

export const formatOrientation = (image: IImage) => {
	if (image && image.formats) {
		const { x_dimension, y_dimension } = orOriginal(image, '1024x768');
		if (x_dimension > y_dimension) {
			return imageOrientConsts.landscape;
		} else if (x_dimension === y_dimension) {
			return imageOrientConsts.square;
		} else {
			return imageOrientConsts.portrait;
		}
	} else {
		return '';
	}
};

export const isBadlyOriented = (image: IImage) => {
	const orient = formatOrientation(image);
	if (orient !== imageOrientConsts.landscape) {
		return true;
	}
	return false;
};

const diffDimensions = (f1: string, f2: string) => {
	const [w1, h1] = f1.split('x').map((s) => parseInt(s, 10)) as [number, number];
	const [w2, h2] = f2.split('x').map((s) => parseInt(s, 10)) as [number, number];
	return [w1 - w2, h1 - h2];
};

export const smallestGreaterThanOrEq = (image: IImage, minimum: string) => {
	let best = image.formats[image.original];
	let [bestDw, bestDh] = [99999999, 99999999];

	entriesOf(image.formats).forEach(([size, format]) => {
		const [dw, dh] = diffDimensions(size as string, minimum);
		if (dw >= 0 && dh >= 0 && dw < bestDw && dh < bestDh) {
			best = format;
			[bestDw, bestDh] = [dw, dh];
		}
	});

	return best;
};

export const orOriginal = (image: IImage, format: string) => {
	return image.formats[format] ? image.formats[format] : image.formats[image.original];
};

export const displayableTags = (image: IImage) => {
	if (!image || !image.quality_score || isEmpty(image.quality_score.tags)) {
		return [];
	}
	const tags = image.quality_score.tags || [];
	const displayTags = tags.filter((tag, index) => (
		index < ImageTagLimits.maxNumVisible && tag.confidence >= ImageTagLimits.minConfidence)
	);
	return displayTags;
};

export const countTags = (image: IImage) => {
	return displayableTags(image).length;
};

export const getDimensionsFromFileData = (file: File): Promise<{ width: number, height: number }> => {
	const image = new Image();
	return new Promise((res) => {
		try {
			image.onload = () => {
				const { naturalWidth: width, naturalHeight: height } = image;
				URL.revokeObjectURL(image.src);
				res({ width, height });
			};
			image.onerror = () => res({ width: 1, height: 1 });
			image.src = URL.createObjectURL(file);
		} catch (e) {
			logging.error(e);
			res({ width: 1, height: 1 });
		}
	});
};

export const upload = (api: IApi, requests: Array<{ id: string; presigned: IS3PresignedPost }>, files: File[], onProgress: (args: any) => any) => {
	return requests.map(async (request, i) => {
		const { presigned: { fields }, id } = request;
		let previousPercent = 0;
		const result = await api.images.uploadToBucket({
			...request.presigned, fields: { ...fields, acl: 'public-read', 'Content-Type': files[i].type, file: files[i] }
		},
			(event) => {
				const percent = Math.round((event.loaded * 100) / event.total);
				const deltaOfTotal = (percent - previousPercent) / files.length;
				previousPercent = percent;
				onProgress(deltaOfTotal);
			}).catch((e) => {
				logging.error(e);
				return { result: 'error', error: e };
			});
		return { id, result };
	});
};

export const diffImageSet = (next: IImage[]) => {
	return next.reduce((diffed, img, i) => {
		if (img.sort_order !== i) diffed.push({ ...img, sort_order: i });
		return diffed;
	}, [] as IImage[]);
};
